import { FaBell } from "react-icons/fa";
import { track } from "@amplitude/analytics-browser";
import type { Store } from "../../../__generated__/graphql";
import type { PackQuery } from "../../../__generated__/graphql";
import type { ExposeType } from "../../../utils/store-expose";
import TransitionWrapper from "../../../components/common/transition-wrapper";
import { ReferralShareButton } from "./referral-share-button";

interface StoreBottomCtaProps {
  cartLength: number;
  totalAmount: number;
  totalDiscount: number;
  store: Store | null | undefined;
  packData: PackQuery | undefined;
  isTimeSaling: boolean;
  isPackSetted: boolean;
  exposeType: ExposeType;
  isPackSoldOut: boolean;
  onPurchase: () => void;
  onPackPurchase: (isTomorrow: boolean) => void;
  onNoti: (
    storeId: number,
    isLiked: boolean | null,
    storeTitle?: string
  ) => void;
}

const StoreBottomCta = ({
  cartLength,
  totalAmount,
  totalDiscount,
  store,
  packData,
  isTimeSaling,
  isPackSetted,
  exposeType,
  isPackSoldOut,
  onPurchase,
  onPackPurchase,
  onNoti,
}: StoreBottomCtaProps) => {
  const packPrice = packData?.pack?.price ?? 0;
  const packUserPrice = packData?.pack?.userPrice ?? 0;
  const packDiscount = packPrice - packUserPrice;

  return (
    <>
      {/* 카트 있을 때: 실시간 구매하기 CTA */}
      <div
        className={`px-5 py-4 w-full fixed bottom-0 max-w-[742px] bg-white z-50 transition-transform duration-300 ease-in-out ${
          cartLength > 0 ? "translate-y-0" : "translate-y-[150%]"
        }`}
      >
        <div className="absolute left-0 top-[-2.7rem] w-full h-[43px] bg-[#282828] rounded-tl-[10px] rounded-tr-[10px] text-white text-sm font-semibold flex justify-center items-center">
          <span className="text-[#fd4242] mr-1">
            {(totalDiscount || 0).toLocaleString()}원
          </span>{" "}
          할인받았어요!
        </div>
        <div className="flex w-full gap-2">
          {store && (
            <ReferralShareButton
              store={store}
              wrapperClassName="border-blue-500"
              onClick={() =>
                track("매장 공유하기 클릭", {
                  storeId: store.id,
                  어디서: "실시간구매 탭",
                })
              }
            />
          )}
          <TransitionWrapper
            onClick={onPurchase}
            scale={0.95}
            opacity={0.8}
            className="w-5/6 h-[60px] flex items-center justify-center bg-[#1562fc] rounded-lg border"
          >
            <div className="text-center flex items-center space-x-1">
              <span className="text-white font-bold leading-snug">
                {totalAmount.toLocaleString()}원
              </span>
              <span className="text-white text-base font-semibold leading-snug">
                구매하기
              </span>
            </div>
          </TransitionWrapper>
        </div>
      </div>

      {/* 카트가 비었을 때 상황별 CTA */}
      {cartLength === 0 && (
        <>
          {/* 실시간 판매❌ & 예약판매 ✅: 예약 하기 CTA */}
          {!isTimeSaling &&
            isPackSetted &&
            exposeType !== "X" &&
            !isPackSoldOut &&
            store && (
              <div className="px-5 py-4 w-full fixed bottom-0 max-w-[742px] bg-white z-50">
                <div className="absolute left-0 top-[-2.7rem] w-full h-[43px] bg-[#282828] rounded-tl-[10px] rounded-tr-[10px] text-white text-sm font-semibold flex justify-center items-center">
                  <span className="text-[#fd4242] mr-1">
                    {packDiscount.toLocaleString()}원
                  </span>{" "}
                  할인받았어요!
                </div>
                {exposeType === "오늘" && (
                  <div className="flex w-full gap-2">
                    <ReferralShareButton
                      store={store}
                      onClick={() =>
                        track("매장 공유하기 클릭", {
                          storeId: store.id,
                          어디서: "예약팩 탭",
                        })
                      }
                      wrapperClassName="border-green-500"
                    />
                    <TransitionWrapper
                      onClick={() => onPackPurchase(false)}
                      scale={0.95}
                      opacity={0.8}
                      className="w-5/6 h-[60px] flex items-center justify-center rounded-lg border bg-green-500"
                    >
                      <div className="text-center flex items-center space-x-1">
                        <span className="text-white font-bold leading-snug">
                          {packData?.pack?.userPrice?.toLocaleString()}원
                        </span>
                        <span className="text-white text-base font-semibold leading-snug">
                          구매하기
                        </span>
                      </div>
                    </TransitionWrapper>
                  </div>
                )}

                {exposeType === "내일" && (
                  <div className="flex w-full gap-2">
                    <ReferralShareButton
                      store={store}
                      onClick={() =>
                        track("매장 공유하기 클릭", {
                          storeId: store.id,
                          어디서: "예약팩 탭 (내일예약)",
                        })
                      }
                      wrapperClassName="border-pink-500"
                    />
                    <TransitionWrapper
                      onClick={() => onPackPurchase(true)}
                      scale={0.95}
                      opacity={0.8}
                      className="w-5/6 h-[60px] flex items-center justify-center rounded-lg border bg-pink-500"
                    >
                      <div className="text-center flex items-center space-x-1">
                        <span className="text-white font-bold leading-snug">
                          {packData?.pack?.userPrice?.toLocaleString()}원
                        </span>
                        <span className="text-white text-base font-semibold leading-snug">
                          내일픽업 예약하기
                        </span>
                      </div>
                    </TransitionWrapper>
                  </div>
                )}
              </div>
            )}

          {/* 실시간 판매❌ & 예약판매 ❌: 알림 받기 CTA */}
          {!isTimeSaling &&
            (!isPackSetted || exposeType === "X" || isPackSoldOut) &&
            !store?.isLiked &&
            store && (
              <div className="px-5 py-4 w-full fixed bottom-0 max-w-[742px] bg-white z-50">
                <div className="absolute left-0 top-[-2.7rem] w-full h-[43px] bg-[#282828] rounded-tl-[10px] rounded-tr-[10px] text-white text-sm font-semibold flex justify-center items-center">
                  <span className="text-sky-400 mr-1">
                    {(store?.likes?.length || 0).toLocaleString()}명
                  </span>{" "}
                  이 알림 받는 중
                </div>
                <div className="flex flex-col gap-2">
                  <TransitionWrapper
                    onClick={(e) => {
                      e.stopPropagation();
                      onNoti(
                        store.id,
                        store.isLiked ?? null,
                        store.title ?? undefined
                      );
                    }}
                    scale={0.95}
                    opacity={0.8}
                    className="w-full h-[60px] flex items-center justify-center rounded-lg border bg-sky-500 gap-2"
                  >
                    <FaBell className="text-white text-sm" />
                    <div className="text-center flex items-center space-x-1">
                      <span className="text-white font-bold leading-snug">
                        오픈 알림받기
                      </span>
                    </div>
                  </TransitionWrapper>
                </div>
              </div>
            )}
        </>
      )}
    </>
  );
};

export default StoreBottomCta;
